import React, { PropTypes } from 'react';
import { Input, Form, Button } from 'semantic-ui-react';
import Datepicker from 'common/components/Datepicker';
import Dropdown from 'common/components/Dropdown';
import cx from 'classnames';
import moment from 'moment';
import { DATE_FORMAT } from 'common/components/PreplanificationSelection';
import Big from 'big.js';
import {
  paymentConditionOptions,
  invoicePaymentFrequencyOptions,
} from '../../index';

const constraintDecimals = (value, decimals) => {
  if (value) {
    return Big(value).toFixed(decimals);
  } else {
    return value;
  }
};

const computeLineTotalTTC = line => {
  const ht = constraintDecimals(line.total_ht, 2);
  const tvaRate = line.tva_rate;

  if (ht && tvaRate) {
    const ttc = Big(ht).times(
      Big(tvaRate)
        .div(100)
        .plus(1)
    );
    const tvaAmount = Big(ht).times(Big(tvaRate).div(100));
    line.tva_amount = tvaAmount.toFixed(2);
    line.total_ttc = ttc.toFixed(2);
  }

  return line;
};

const computeLineTotalHT = (line, indexationPercentage) => {
  line.unit_price_ht = parseFloat(line.unit_price_ht);
  line.unit_price_ht += indexationPercentage * line.unit_price_ht / 100;
  const quantity = constraintDecimals(line.quantity, 2);
  const puHT = constraintDecimals(line.unit_price_ht, 4);

  if (quantity && puHT) {
    const ht = Big(puHT).times(Big(quantity));
    line.total_ht = ht.toFixed(2);
  }

  return computeLineTotalTTC(line);
};

const RenewInvoice = ({
  onValueChange,
  invoiceLines,
  indexationPercentage,
  invoiceCreateAction,
  invoiceDate,
  invoicePaymentDate,
  invoicePaymentCondition,
  invoicePaymentFrequency,
  onInvoiceLinesChange,
  errors,
}) => {
  const isPaymentDateDisable = () => {
    const selectedValue = paymentConditionOptions.find(
      r => r.value === invoicePaymentCondition
    );

    if (selectedValue) {
      return selectedValue.inputDateDisabled;
    }
    return false;
  };

  const itemInvoicePaymentConditionClassName = value => {
    const active = invoicePaymentCondition === value;
    return cx('item', {
      active: active,
      selected: active,
    });
  };

  const renderInvoicePaymentConditionDefault = () => {
    const selectedValue = paymentConditionOptions.find(
      r => r.value === invoicePaymentCondition
    );
    if (selectedValue) {
      return <div className="text">{selectedValue.text}</div>;
    }
    return <div className="default text" />;
  };

  const renderInvoicePaymentConditionItem = item => (
    <div
      key={item.value}
      data-value={item.value}
      className={itemInvoicePaymentConditionClassName(item.value)}
    >
      {item.text}
    </div>
  );

  const itemInvoicePaymentFrequencyClassName = value => {
    const active = invoicePaymentFrequency === value;
    return cx('item', {
      active: active,
      selected: active,
    });
  };

  const renderInvoicePaymentFrequencyDefault = () => {
    const selectedValue = invoicePaymentFrequencyOptions.find(
      r => r.value === invoicePaymentFrequency
    );
    if (selectedValue) {
      return <div className="text">{selectedValue.text}</div>;
    }
    return <div className="default text" />;
  };

  const renderInvoicePaymentFrequencyItem = item => (
    <div
      key={item.value}
      data-value={item.value}
      className={itemInvoicePaymentFrequencyClassName(item.value)}
    >
      {item.text}
    </div>
  );

  const updatePaymentDate = (date, condition) => {
    let updatedDate = moment(date, DATE_FORMAT).add(
      'days',
      condition.numberOfDays
    );

    if (condition.endOfMonth) {
      updatedDate.endOf('month');
    }
    onValueChange(
      'invoicingInformations.balanceInvoicePaymentDate',
      updatedDate.format(DATE_FORMAT)
    );
  };

  const onDropdownChange = (value, text, choice) => {
    onValueChange(
      'invoicingInformations.balanceInvoicePaymentCondition',
      value
    );
    const selectedValue = paymentConditionOptions.find(r => r.value === value);
    if (selectedValue && selectedValue.value !== 'CUSTOM' && invoiceDate) {
      updatePaymentDate(invoiceDate, selectedValue);
    }
  };

  const onInvoicePaymentFrequencyDropdownChange = (value, text, choice) => {
    onValueChange(
      'invoicingInformations.balanceInvoicePaymentFrequency',
      parseInt(value, 0)
    );
  };

  const fieldClass = fieldName =>
    cx('field required', {
      error: errors.includes(fieldName),
    });

  const invoiceDateLabel = isCreateAction => {
    return isCreateAction ? "Date d'émission estimée" : "Date d'émission";
  };

  const invoicePaymentDateLabel = isCreateAction => {
    return isCreateAction ? 'Date de réglement estimée' : 'Date de réglement';
  };

  const onInvoiceDateChange = date => {
    onValueChange(
      'invoicingInformations.balanceInvoiceDate',
      moment(date.date).format(DATE_FORMAT)
    );
    const selectedValue = paymentConditionOptions.find(
      r => r.value === invoicePaymentCondition
    );
    if (selectedValue && selectedValue.value !== 'CUSTOM') {
      updatePaymentDate(moment(date.date).format(DATE_FORMAT), selectedValue);
    }
  };

  const onInvoicePaymentDateChange = date => {
    onValueChange(
      'invoicingInformations.balanceInvoicePaymentDate',
      moment(date.date).format(DATE_FORMAT)
    );
  };

  const onCreateInvoiceActionChange = event => {
    onValueChange(
      'invoicingInformations.balanceInvoiceCreateAction',
      event.target.value === 'true'
    );
    if (event.target.value !== 'true') {
      onValueChange(
        'invoicingInformations.balanceInvoiceDate',
        moment().format(DATE_FORMAT)
      );
      const selectedValue = paymentConditionOptions.find(
        r => r.value === invoicePaymentCondition
      );
      if (selectedValue && selectedValue.value !== 'CUSTOM') {
        updatePaymentDate(moment().format(DATE_FORMAT), selectedValue);
      }
    }
  };

  const onTemporaryPercentageChange = event => {
    let value = event.target.value;
    if (value && value !== '') {
      value = parseInt(event.target.value, 0);
      onValueChange('invoicingInformations.indexationPercentage', value);
    } else if (typeof value !== 'undefined' && value === '') {
      onValueChange('invoicingInformations.indexationPercentage', value);
    }
  };

  const onPercentageChange = event => {
    let value = indexationPercentage;
    if (value && value !== '') {
      value = parseInt(value, 0);
      onValueChange('invoicingInformations.indexationPercentage', value);
      let lines = [...invoiceLines];
      lines.map(l => computeLineTotalHT(l, value));
      onInvoiceLinesChange(lines);
    }
  };

  return (
    <div>
      <Form.Group widths="equal">
        <Form.Field>
          <label>Indice d'indexation en %</label>
          <Input
            name="indexationPercentage"
            value={indexationPercentage}
            onChange={onTemporaryPercentageChange}
            action
          >
            <input />
            <Button onClick={onPercentageChange}>Mettre à jour</Button>
          </Input>
        </Form.Field>
        <Form.Field required>
          <label>Payable</label>
          <Dropdown
            name="invoicePaymentFrequency"
            value={invoicePaymentFrequency}
            items={invoicePaymentFrequencyOptions}
            renderDefault={renderInvoicePaymentFrequencyDefault}
            renderItem={renderInvoicePaymentFrequencyItem}
            onChange={onInvoicePaymentFrequencyDropdownChange}
          />
        </Form.Field>
      </Form.Group>
      <Form.Group widths="equal">
        <Form.Field required>
          <label>Action à appliquer sur la facture</label>
          <Button.Group>
            <Button
              type="button"
              positive={invoiceCreateAction}
              value={true}
              onClick={onCreateInvoiceActionChange}
            >
              Créer
            </Button>
            <Button
              type="button"
              positive={!invoiceCreateAction}
              value={false}
              onClick={onCreateInvoiceActionChange}
            >
              Emettre
            </Button>
          </Button.Group>
        </Form.Field>
        <Form.Field
          required
          className={fieldClass('invoicingInformations.balanceInvoiceDate')}
        >
          <label>{invoiceDateLabel(invoiceCreateAction)}</label>
          <Datepicker
            identifier="invoiceDate"
            name="invoiceDate"
            value={invoiceDate}
            onChange={onInvoiceDateChange}
            placeholder="JJ/MM/AAAA"
            disabled={!invoiceCreateAction}
          />
        </Form.Field>
        <Form.Field required>
          <label>Conditions de règlement</label>
          <Dropdown
            name="invoicePaymentCondition"
            value={invoicePaymentCondition}
            items={paymentConditionOptions}
            renderDefault={renderInvoicePaymentConditionDefault}
            renderItem={renderInvoicePaymentConditionItem}
            onChange={onDropdownChange}
          />
        </Form.Field>
        <Form.Field
          required
          className={fieldClass(
            'invoicingInformations.balanceInvoicePaymentDate'
          )}
        >
          <label>{invoicePaymentDateLabel(invoiceCreateAction)}</label>
          <Datepicker
            identifier="invoicePaymentDate"
            name="invoicePaymentDate"
            value={invoicePaymentDate}
            onChange={onInvoicePaymentDateChange}
            placeholder="JJ/MM/AAAA"
            disabled={isPaymentDateDisable()}
          />
        </Form.Field>
      </Form.Group>
    </div>
  );
};

RenewInvoice.propTypes = {
  indexationPercentage: PropTypes.number,
  invoiceCreateAction: PropTypes.bool.isRequired,
  invoiceDate: PropTypes.string,
  invoicePaymentDate: PropTypes.string,
  invoicePaymentCondition: PropTypes.string.isRequired,
  invoicePaymentFrequency: PropTypes.number.isRequired,
  errors: PropTypes.array,
};

export default RenewInvoice;



// WEBPACK FOOTER //
// ./src/modules/saas/aast/case-contract/renew/components/RenewInvoice.js